import {
  ChatCustomBubbleContainer,
  ChatMessageContainer,
  MessageContextMenuTarget,
} from './MessageContainers';
import type { RenderContext } from './RenderContext';
import { resolveMessagePlan } from './messagePlan';

import { CustomMessageProps, ICustomMessage } from '@/components/chat';
import { BUBBLE_ENTRIES } from '@/components/chat/message-types/bubbleEntries';
import { applyEntry, resolveEntry, resolveFillRow } from '@/components/chat/message-types/registry';
import { ROW_ENTRIES } from '@/components/chat/message-types/rowEntries';

/**
 * Builds the GiftedChat `renderMessage` callback.
 *
 * Whole-row custom bubbles come from the registry; anything it does not claim
 * falls through to the default container. Adding a whole-row bubble is a
 * registry entry, not a branch here.
 */
export function makeRenderMessage(ctx: RenderContext) {
  // @ts-expect-error GiftedChat passes key; not in CustomMessageProps
  return function renderMessage({ key, ...props }: CustomMessageProps) {
    return (
      <MessageContextMenuTarget
        message={props.currentMessage as ICustomMessage}
        onContextMenu={ctx.onMessageContextMenu}>
        {renderMessageRow(props, ctx)}
      </MessageContextMenuTarget>
    );
  };
}

function renderMessageRow(props: CustomMessageProps, ctx: RenderContext) {
  const currentMsg = props.currentMessage as ICustomMessage;

  const entry = resolveEntry(ROW_ENTRIES, currentMsg, ctx);

  if (entry) {
    /** What this row renders without any entry — an entry's `'default'`
     * fallback degrades to it, which is the only path guaranteed not to
     * share whatever just threw. */
    const defaultRow = <ChatMessageContainer {...props} fillRow={false} />;

    const resolution = applyEntry(entry, currentMsg, ctx, props, defaultRow);

    if (resolution.kind === 'hidden') {
      return <></>;
    }

    if (resolution.kind === 'element') {
      const position = entry.container.position?.(currentMsg, ctx);

      return (
        <ChatCustomBubbleContainer position={position}>
          {resolution.element}
        </ChatCustomBubbleContainer>
      );
    }

    // `fallthrough`: the entry claimed the message but its payload could not
    // paint the bubble. The row renders plain text and stops here — a
    // claimed message never widens the row on the strength of some other
    // bubble's condition.
    if (resolution.kind === 'fallthrough') {
      return <ChatMessageContainer {...props} fillRow={false} />;
    }
  }

  const plan = resolveMessagePlan(currentMsg, ctx);

  /**
   * The row asks the registry which bubble is about to render and whether it
   * wants the row widened, so the two levels cannot disagree — the row commits
   * to its width before the bubble level runs.
   *
   * The caption gate is row layout, not bubble selection: a caption pairs its
   * label with a content-width bubble, so a captioned row never widens
   * whichever bubble it hosts. The entry only declares that it *wants* a
   * full-width row.
   */
  const fillRow =
    (resolveFillRow(BUBBLE_ENTRIES, currentMsg, ctx) && !plan.isCaptionViewer) ||
    plan.isPendingProcessing;

  return <ChatMessageContainer {...props} fillRow={fillRow} />;
}
